// src/features/hoa/hooks.js
//
// Data hooks for the HOA module. Every hook reads Firestore directly, on the
// usePlatformCategories template — getDocs/getDoc, a cancelled guard, and a
// catch that clears loading. The app uses no snapshot listeners.
//
// HOOK CONTRACTS ARE FINAL — do not change return shapes. Components depend
// on these shapes, so the snake_case-to-camelCase and Timestamp-to-Date
// adapters live INSIDE the hooks; no page ever sees a raw document.
//
// Several surfaces have no collection behind them at all — HOA requests, the
// community budget, management companies, invite previews. Those hooks return
// empty and say so. Nothing here fabricates a row, a total or a date to fill
// a gap in the data model.

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';

// Module-level, mirroring src/api/index.js:38 — the app's established shape
// for callables.
const _functions = getFunctions(app);

/**
 * Reads one subcollection of hoa_communities/{hoaId} on the
 * usePlatformCategories template: getDocs, cancelled guard, catch clearing
 * loading. No-ops with loading:false when hoaId is absent.
 *
 * `map` must be a module-level function — an inline arrow would change
 * identity every render and re-run the effect forever.
 *
 * Note on orderBy: Firestore omits documents that lack the ordered field
 * entirely, so a mapper reading that field can rely on it being present.
 */
function useHoaCollection(hoaId, subcollection, { field, direction = 'asc', map }) {
  const [state, setState] = useState({
    data: null,
    loading: Boolean(hoaId),
    error: null,
  });

  useEffect(() => {
    if (!hoaId) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    const ref = collection(db, 'hoa_communities', hoaId, subcollection);
    getDocs(field ? query(ref, orderBy(field, direction)) : ref)
      .then((snap) => {
        if (cancelled) return;
        setState({
          data: snap.docs.map((d) => map(d.id, d.data())),
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        // Every HOA read is gated on isHoaMember(); a rejection means the
        // caller is not a member. Surfaced, never swallowed.
        console.error(`useHoaCollection: ${subcollection} read failed`, error);
        if (!cancelled) setState({ data: null, loading: false, error });
      });

    return () => { cancelled = true; };
  }, [hoaId, subcollection, field, direction, map]);

  return state;
}

// Firestore Timestamp -> Date at the mapping boundary. Pages hand these
// straight to date-fns, which rejects anything else.
const toDate = (v) => (typeof v?.toDate === 'function' ? v.toDate() : null);

/**
 * Composes an event's start from the stored event_date + event_time.
 *
 * event_date is a plain 'YYYY-MM-DD' string: toolHandlers.js passes it
 * through untouched while calling .toDate() on created_at, so it
 * is not a Timestamp. A Timestamp is still accepted in case older docs carry
 * one. Parsed into local calendar components on purpose — new Date('2026-03-14')
 * is UTC midnight and renders as the 13th everywhere in the US.
 *
 * Returns null when the date cannot be read; the caller drops the event
 * rather than inventing a date for it.
 */
function parseCalendarDate(dateVal, hours = 0, minutes = 0) {
  if (!dateVal) return null;
  if (typeof dateVal?.toDate === 'function') return dateVal.toDate();
  if (dateVal instanceof Date) return dateVal;
  if (typeof dateVal !== 'string') return null;

  const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(dateVal.trim());
  if (!ymd) return null;

  return new Date(
    Number(ymd[1]),
    Number(ymd[2]) - 1,
    Number(ymd[3]),
    hours,
    minutes
  );
}

function composeEventStart(dateVal, timeVal) {
  let hours = 0;
  let minutes = 0;
  const hm = typeof timeVal === 'string'
    ? /^(\d{1,2}):(\d{2})\s*(am|pm)?/i.exec(timeVal.trim())
    : null;
  if (hm) {
    const raw = Number(hm[1]);
    const suffix = hm[3]?.toLowerCase();
    if (suffix === 'pm') hours = raw === 12 ? 12 : raw + 12;
    else if (suffix === 'am') hours = raw === 12 ? 0 : raw;
    else hours = raw;
    minutes = Number(hm[2]);
  }
  return parseCalendarDate(dateVal, hours, minutes);
}

// ═══════════════════════════════════════════════════════════════════════
// Firestore -> module contract mappers
//
// snake_case in, camelCase out. Nothing here invents a value: a field the
// document does not carry becomes null/false, never a placeholder.
// ═══════════════════════════════════════════════════════════════════════
function mapAnnouncement(id, d) {
  return {
    id,
    title: d.title || '',
    body: d.body || '',
    pinned: d.pinned === true,
    // Live docs carry no priority; 'info' is the neutral badge, not a claim.
    priority: d.priority || 'info',
    createdAt: toDate(d.created_at),
    // Filled in by the hook from the community name — see useHoaAnnouncements.
    createdByName: d.created_by_name || null,
  };
}

function mapEvent(id, d) {
  const startAt = composeEventStart(d.event_date, d.event_time);
  return {
    id,
    title: d.title || '',
    description: d.description || '',
    startAt,
    // No end time is stored. endAt mirrors startAt rather than guessing a
    // duration; the pages only read it for parity with the type.
    endAt: startAt,
    location: d.location || null,
    allDay: !d.event_time,
  };
}

function mapMember(id, d) {
  return {
    userId: id,
    role: d.hoa_role || 'resident',
    joinedAt: toDate(d.joined_at),
    homeId: d.property_id ?? null,
    addressId: d.address_id ?? null,
    // Snapshotted onto the junction by joinHoaWithCode. Members who joined
    // before that amendment have no display_name, and it stays null here —
    // the substitute belongs at the render boundary, not in the mapper.
    displayName: d.display_name || null,
  };
}

const MEMBERS_QUERY = { map: mapMember };

function mapMessage(id, d) {
  return {
    id,
    // sender_id is written by sendMessage below. Messages predating it have
    // none, so they simply never match the current user and render as
    // someone else's — which is the honest outcome without an author.
    senderId: d.sender_id ?? null,
    senderName: d.sender_name || null,
    text: d.message || '',
    photos: Array.isArray(d.photos) ? d.photos : [],
    createdAt: toDate(d.created_at),
    // senderRole is resolved in useHoaChat from the members junction —
    // chat_messages carries no role field.
  };
}

const MESSAGES_QUERY = { field: 'created_at', direction: 'asc', map: mapMessage };

// hoa_role values that should render with the ADMIN treatment in chat.
const ADMIN_HOA_ROLES = new Set([
  'board_admin',
  'management_company_admin',
  'homecierge_staff',
]);

// One junction doc per (community, user), cached for the session like
// _hoaCache below and for the same reason: HoaAdminRoute, AdminBanner and
// HoaAdminDashboard all ask for the role on a single /hoa-admin navigation,
// and AdminBanner asks twice. Without this that is four reads of one document.
// Cleared on reload, so a promotion to board_admin lands on the next load.
const _roleCache = new Map();

// ═══════════════════════════════════════════════════════════════════════
// useHoaRole() — the caller's hoa_role at their active community
//
// Reads hoa_communities/{cid}/members/{uid}.hoa_role, the same junction
// firestore.rules gates every HOA read on and the same one createHoaInvite,
// addRosterAddress and setRosterAddressStatus authorize against. That is the
// only place adminship is recorded.
//
// It previously derived from users/{uid}.role === 'hoa_admin'. That field is a
// scalar with no community on it: it could not say WHICH community a person
// administers, it is not what the server checks, and a board admin created by
// joinHoaWithCode never gets it set. A user could hold it and be refused by
// every callable, or hold a board_admin junction doc and be shown nothing.
//
// Absent doc means resident, not error. A non-member reads no junction doc and
// a member with no hoa_role written is a resident by default — both are
// ordinary states, so neither produces an error the caller has to branch on.
//
// The read is asynchronous, so `loading` is part of the contract. A consumer
// that redirects on the result MUST wait for it; treating the initial
// 'resident' as an answer bounces a real admin off their own dashboard.
// ═══════════════════════════════════════════════════════════════════════
export function useHoaRole() {
  const { user, profile } = useAuth();
  const uid = user?.uid ?? null;
  const cid = profile?.hoa_community_id ?? null;
  const cacheKey = uid && cid ? `${cid}/${uid}` : null;

  const [state, setState] = useState(() => (
    cacheKey && _roleCache.has(cacheKey)
      ? { role: _roleCache.get(cacheKey), loading: false }
      : { role: 'resident', loading: Boolean(cacheKey) }
  ));

  useEffect(() => {
    if (!cacheKey) {
      setState({ role: 'resident', loading: false });
      return undefined;
    }
    if (_roleCache.has(cacheKey)) {
      setState({ role: _roleCache.get(cacheKey), loading: false });
      return undefined;
    }

    let cancelled = false;
    setState({ role: 'resident', loading: true });

    getDoc(doc(db, 'hoa_communities', cid, 'members', uid))
      .then((snap) => {
        const role = snap.exists() ? snap.data().hoa_role || 'resident' : 'resident';
        _roleCache.set(cacheKey, role);
        if (cancelled) return;
        setState({ role, loading: false });
      })
      .catch((error) => {
        // A member may read their own junction doc, so a rejection means the
        // profile names a community this user does not belong to. Resident is
        // the honest answer — they administer nothing. Cached like the success
        // path so a stale profile does not re-request on every navigation.
        console.error('useHoaRole: membership read failed', error);
        _roleCache.set(cacheKey, 'resident');
        if (!cancelled) setState({ role: 'resident', loading: false });
      });

    return () => { cancelled = true; };
  }, [cacheKey, uid, cid]);

  return state;
}

// ═══════════════════════════════════════════════════════════════════════
// useCurrentHoaRole() — scalar role, for render-only consumers
//
// Contract unchanged: a plain hoa_role string. Reads 'resident' while the
// junction is in flight, which is correct for anything that only decides what
// to draw. Anything that REDIRECTS must use useHoaRole() and honour loading.
// ═══════════════════════════════════════════════════════════════════════
export function useCurrentHoaRole() {
  return useHoaRole().role;
}

// ═══════════════════════════════════════════════════════════════════════
// useIsHoaAdmin() — convenience boolean, same caveat as above
//
// 'board_admin' only, matching what the callables actually enforce. The wider
// ADMIN_HOA_ROLES set governs chat styling, not permission: showing a
// management_company_admin an admin surface every server call then refuses is
// worse than not showing it.
// ═══════════════════════════════════════════════════════════════════════
export function useIsHoaAdmin() {
  return useHoaRole().role === 'board_admin';
}

// ═══════════════════════════════════════════════════════════════════════
// useActiveHoa()
//
// Reads hoa_communities/{cid} for the community named on the user profile.
//
// The live doc carries: name, address, total_homes, monthly_dues, admin_uid,
// created_at. It has NO city/state/zip and NO budget, so those stay absent
// on the returned object — the pages guard for them and omit the line.
//
// `address` on the doc is the COMMUNITY's street address, not the resident's.
// It is deliberately not mapped onto activeAddress; conflating the two would
// tell a resident they live at the clubhouse.
// ═══════════════════════════════════════════════════════════════════════

// One community doc per id, cached for the session like platform_categories.
// useActiveHoa runs in Layout, the page, CommunityBudgetPill, useHoaBudget
// and useManagementCompany at once; without this a single HOA screen refetches
// the same document five times.
const _hoaCache = new Map();

function mapCommunity(id, d) {
  return {
    id,
    name: d.name || 'Your HOA',
    // monthly_dues is the live field; duesConfig.amount is the module
    // contract. The figure exists and was only ever unmapped. When the field
    // is absent the key stays undefined so the pages render no figure.
    duesConfig: d.monthly_dues != null ? { amount: d.monthly_dues } : undefined,
  };
}

export function useActiveHoa() {
  const { profile } = useAuth();
  const cid = profile?.hoa_community_id ?? null;

  const [state, setState] = useState(() => ({
    hoa: cid ? _hoaCache.get(cid) ?? null : null,
    loading: Boolean(cid) && !_hoaCache.has(cid),
    error: null,
  }));

  useEffect(() => {
    if (!cid) {
      setState({ hoa: null, loading: false, error: null });
      return undefined;
    }
    if (_hoaCache.has(cid)) {
      setState({ hoa: _hoaCache.get(cid), loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState({ hoa: null, loading: true, error: null });

    getDoc(doc(db, 'hoa_communities', cid))
      .then((snap) => {
        const hoa = snap.exists() ? mapCommunity(cid, snap.data()) : null;
        // Cached unconditionally, null included. A missing community is a
        // stable answer; without this every mount refetches it.
        _hoaCache.set(cid, hoa);
        if (!cancelled) setState({ hoa, loading: false, error: null });
      })
      .catch((error) => {
        // Reads are gated on isHoaMember(). A rejection here means the
        // profile names a community the caller is not a member of — also a
        // stable answer, so it is cached rather than retried five times per
        // navigation. Like platform_categories, the cache clears on reload;
        // membership granted mid-session needs one.
        console.error('useActiveHoa: community read failed', error);
        _hoaCache.set(cid, null);
        if (!cancelled) setState({ hoa: null, loading: false, error });
      });

    return () => { cancelled = true; };
  }, [cid]);

  return {
    ...state,
    // Derived from the profile, not the fetch, so the "My HOA" nav entry in
    // Layout does not flicker out and back while the doc loads.
    hasHoa: Boolean(cid),
    activeAddress: profile?.address || null,
    activeHomeId: profile?.primary_property_id || null,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useManagementCompany(hoaId)
//
// Returns the management company record for the given HOA, or null if
// the HOA is self-managed. Exposes primary contact info for the admin
// dashboard "Managed by..." card.
// ═══════════════════════════════════════════════════════════════════════
// No management_companies collection exists and the community doc carries no
// managementCompanyId, so every HOA is self-managed as far as the data is
// concerned. ManagementCompanyCard already renders nothing in that case.
export function useManagementCompany() {
  return {
    managementCompany: null,
    hasManagementCompany: false,
    loading: false,
    error: null,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaEvents(hoaId) — hoa_communities/{id}/events
// ═══════════════════════════════════════════════════════════════════════
const EVENTS_QUERY = { field: 'event_date', direction: 'asc', map: mapEvent };

export function useHoaEvents(hoaId) {
  const { data, loading, error } = useHoaCollection(hoaId, 'events', EVENTS_QUERY);

  const events = useMemo(() => {
    if (!data) return [];
    // An event whose date will not parse cannot be placed on a calendar, and
    // inventing one is not an option. Dropped loudly rather than crashing
    // date-fns downstream.
    const usable = data.filter((e) => e.startAt instanceof Date);
    if (usable.length !== data.length) {
      console.warn(
        `useHoaEvents: dropped ${data.length - usable.length} event(s) with an unreadable event_date`
      );
    }
    return usable;
  }, [data]);

  const createEvent = useCallback(async () => {
    // events is write:false in the rules and has no server writer.
    throw new Error('Creating events is not available yet.');
  }, []);

  return {
    events,
    loading,
    error,
    createEvent,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaAnnouncements(hoaId) — hoa_communities/{id}/announcements
// ═══════════════════════════════════════════════════════════════════════
const ANNOUNCEMENTS_QUERY = {
  field: 'created_at',
  direction: 'desc',
  map: mapAnnouncement,
};

export function useHoaAnnouncements(hoaId) {
  const { hoa } = useActiveHoa();
  const { data, loading, error } = useHoaCollection(
    hoaId,
    'announcements',
    ANNOUNCEMENTS_QUERY
  );

  const communityName = hoa?.name ?? null;

  const announcements = useMemo(() => {
    if (!data) return [];
    return data
      .map((a) => ({
        ...a,
        // Announcements are posted by the board, and the docs carry no
        // author. The community's own name is the truthful attribution.
        createdByName: a.createdByName || communityName || 'Your HOA',
      }))
      // Query order is created_at desc; Array.sort is stable, so lifting the
      // pinned ones keeps recency within each group.
      .sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1));
  }, [data, communityName]);

  const createAnnouncement = useCallback(async () => {
    // announcements is write:false in the rules and has no server writer.
    throw new Error('Posting announcements is not available yet.');
  }, []);

  return {
    announcements,
    loading,
    error,
    createAnnouncement,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// HOA REQUESTS — no collection exists
//
// firestore.rules defines no path for HOA requests under
// hoa_communities/{cid}: not a read rule, not a write rule, nothing. There
// is no server writer either. Both hooks below therefore return an empty
// list rather than querying a collection that does not exist, and the pages
// say so plainly instead of implying the resident simply has none yet.
//
// Landing this feature needs a rules block and a writer first.
// ═══════════════════════════════════════════════════════════════════════
export function useHoaRequests() {
  const createRequest = useCallback(async () => {
    throw new Error('Submitting HOA requests is not available yet.');
  }, []);

  return {
    requests: [],
    loading: false,
    error: null,
    createRequest,
  };
}

export function useHoaAllRequests() {
  const updateRequestStatus = useCallback(async () => {
    throw new Error('Triaging HOA requests is not available yet.');
  }, []);

  return {
    requests: [],
    loading: false,
    error: null,
    updateRequestStatus,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaChat(hoaId) — GROUP chat, one thread per HOA
// ═══════════════════════════════════════════════════════════════════════
export function useHoaChat(hoaId) {
  const { user, profile } = useAuth();
  const uid = user?.uid;
  // sender_name goes on the wire and is rule-required to be a non-empty
  // string, so trim before testing: a whitespace-only first_name falls back
  // to Resident, and an absent last_name yields the first name alone rather
  // than a trailing space-dot. The old 'You' fallback was being written as a
  // sender_name — every reader then saw the author as "You".
  const first = (profile?.first_name || '').trim();
  const lastInitial = (profile?.last_name || '').trim()[0];
  const displayName = first
    ? (lastInitial ? first + ' ' + lastInitial + '.' : first)
    : 'Resident';

  const { data, loading, error } = useHoaCollection(
    hoaId,
    'chat_messages',
    MESSAGES_QUERY
  );

  // The roster supplies both the role (chat_messages has no role field) and a
  // secondary name for authors whose message predates sender_name.
  const { members } = useHoaMembers(hoaId);
  const membersByUid = useMemo(() => {
    const index = new Map();
    members.forEach((m) => index.set(m.userId, m));
    return index;
  }, [members]);

  const [localMessages, setLocalMessages] = useState([]);

  // Reset on community change so a previous HOA's sent messages cannot leak
  // into another community's thread.
  useEffect(() => { setLocalMessages([]); }, [hoaId]);

  const sendMessage = useCallback(async ({ text, photos = [] }) => {
    if (!hoaId) throw new Error('No community to post to.');
    if (!uid) throw new Error('You must be signed in to post.');

    const ref = await addDoc(
      collection(db, 'hoa_communities', hoaId, 'chat_messages'),
      {
        sender_id: uid,
        sender_name: displayName,
        message: text,
        created_at: serverTimestamp(),
      }
    );

    // serverTimestamp() resolves on the server and reads back null locally,
    // and nothing re-reads the thread — there is no snapshot listener — so
    // the optimistic row carries the local send time to keep date-fns fed.
    // The server value is authoritative and replaces this on the next load.
    const optimistic = {
      id: ref.id,
      senderId: uid,
      senderName: displayName,
      senderRole: 'resident',
      text,
      photos,
      createdAt: new Date(),
    };
    setLocalMessages((prev) => [...prev, optimistic]);
    return optimistic;
  }, [hoaId, uid, displayName]);

  const messages = useMemo(() => {
    const stored = (data ?? []).map((m) => {
      const member = m.senderId ? membersByUid.get(m.senderId) : null;
      return {
        ...m,
        // HoaChat calls .split() on this — it must never be null.
        senderName: m.senderName || member?.displayName || 'Resident',
        senderRole: ADMIN_HOA_ROLES.has(member?.role) ? 'admin' : 'resident',
      };
    });
    // Locally sent messages are already ordered after the fetched page: the
    // query is created_at asc and these were written after it resolved.
    return [...stored, ...localMessages];
  }, [data, localMessages, membersByUid]);

  return {
    messages,
    loading,
    error,
    sendMessage,
    currentUserId: uid,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaInvoices(hoaId) — resident's billing history + balance summary
// ═══════════════════════════════════════════════════════════════════════
export function useHoaInvoices(hoaId) {
  const { user } = useAuth();
  const uid = user?.uid;

  const [state, setState] = useState({
    dues: null,
    loading: Boolean(hoaId && uid),
    error: null,
  });

  useEffect(() => {
    if (!hoaId || !uid) {
      setState({ dues: null, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState({ dues: null, loading: true, error: null });

    // member_dues is `allow read: if false` — there is no client read path.
    // The callable verifies membership server-side and returns only this
    // caller's row.
    httpsCallable(_functions, 'getMyHoaDues')({ hoa_community_id: hoaId })
      .then((res) => {
        if (cancelled) return;
        setState({ dues: res?.data?.dues ?? null, loading: false, error: null });
      })
      .catch((error) => {
        console.error('useHoaInvoices: getMyHoaDues failed', error);
        if (!cancelled) setState({ dues: null, loading: false, error });
      });

    return () => { cancelled = true; };
  }, [hoaId, uid]);

  const { dues, loading, error } = state;

  // Always an object, never null. The page gates its spinner on summary, so
  // returning null for a resident with no dues doc would spin forever.
  // Every field is nullable instead: absent dues read as absent.
  const summary = useMemo(() => ({
    balance: typeof dues?.balance === 'number' ? dues.balance : null,
    monthlyAmount:
      typeof dues?.monthly_amount === 'number' ? dues.monthly_amount : null,
    // The callable already normalized this to an ISO string or null; parsing
    // to a local calendar date keeps a due date on the day it names rather
    // than shifting it a day west of UTC.
    nextDueDate: parseCalendarDate(dues?.next_due_date),
    hasDues: Boolean(dues),
  }), [dues]);

  return {
    // There is no invoice ledger in the data model — member_dues is a single
    // balance document per member. No rows are synthesized to fill this, and
    // no year-to-date total is computed from data that does not exist.
    invoices: [],
    summary,
    loading,
    error,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaBudget(hoaId) — community budget with category breakdown
// ═══════════════════════════════════════════════════════════════════════
// There is no expenditures collection and no budget on the community doc.
// summary stays null and CommunityBudgetPill renders a "not available" state
// for it — a permanent "Loading community budget…" would be a lie about work
// that is not happening. Nothing here is computed from data that is absent.
export function useHoaBudget() {
  const addExpenditure = useCallback(async () => {
    throw new Error('Logging expenditures is not available yet.');
  }, []);

  return {
    expenditures: [],
    summary: null,
    loading: false,
    error: null,
    addExpenditure,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaInvite(token)
// ═══════════════════════════════════════════════════════════════════════
// hoa_invites is read,write:false — closed to clients entirely, because an
// invite is redeemed by someone who is not yet a member and so cannot be
// gated on membership. There is no client path to preview one, and no server
// endpoint that returns one. Returns null rather than reading a collection
// the rules will always deny; JoinHoa renders its "invite isn't valid" state.
//
// Previewing an invite needs a callable of its own. Until then the redeem
// path is joinHoaWithCode, which validates the code server-side.
export function useHoaInvite() {
  return {
    invite: null,
    loading: false,
    error: null,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// useAcceptInvite()
// ═══════════════════════════════════════════════════════════════════════
// Redemption is server-owned: joinHoaWithCode writes the membership junction,
// which the client is denied. Wiring accept() to that callable is deliberately
// NOT done here — JoinHoa passes the ?token= query param, and whether that
// value is the same thing as the function's `code` has to be established
// against real invite documents first. Guessing would mint a broken join.
export function useAcceptInvite() {
  const [state, setState] = useState({ loading: false, error: null });

  const accept = useCallback(async () => {
    const err = new Error('Joining by invite link is not available yet.');
    setState({ loading: false, error: err });
    throw err;
  }, []);

  return { accept, ...state };
}

// ═══════════════════════════════════════════════════════════════════════
// useHoaMembers(hoaId)
// ═══════════════════════════════════════════════════════════════════════
export function useHoaMembers(hoaId) {
  // Deliberately unordered: orderBy drops documents missing the sorted field,
  // and the roster must not lose a member over an absent joined_at.
  const { data, loading, error } = useHoaCollection(hoaId, 'members', MEMBERS_QUERY);

  const members = useMemo(() => {
    if (!data) return [];
    // Render boundary: anyone who joined before joinHoaWithCode started
    // snapshotting display_name has none, and a roster row still has to say
    // something. 'Resident' is a label for an unnamed row, not a claimed name.
    return data.map((m) => ({ ...m, displayName: m.displayName || 'Resident' }));
  }, [data]);

  return {
    members,
    // The roster exists at hoa_communities/{cid}/addresses; UI wiring pending.
    // Empty until this reads it, rather than derived from something that is
    // not an address.
    addresses: [],
    loading,
    error,
  };
}
